import { Booking } from '@app/domain/Booking'
import { BookingSlot } from '@app/domain/BookingSlot'
import { Component, Input, OnChanges, OnInit } from '@angular/core'
import { DateTime } from 'luxon'
import { Organisation } from '@app/domain/Organisation'
import { Service } from '@app/domain/Service'
import { Venue } from '@app/domain/Venue'

type AggregatedEntry = {
    name: string
    totalQuantity: number
}

@Component({
    selector: 'app-print-booking-orders',
    templateUrl: './print-booking-orders.component.html',
})
export class PrintBookingOrdersComponent implements OnInit, OnChanges {

    @Input() date!: Date
    @Input() organisation!: Organisation
    @Input() venue!: Venue
    @Input() bookings!: Booking[]
    @Input() service: Service | null = null

    serviceDescription: {
        name: string,
        start: Date,
        end: Date,
    } | null = null

    aggregatedProducts: AggregatedEntry[] = []
    aggregatedModifiers: AggregatedEntry[] = []

    constructor() {}

    ngOnInit() {
        this.injectStylesToIframe()
    }

    ngOnChanges() {
        this.refreshAggregatedLineItems()
    }

    private refreshAggregatedLineItems() {
        const timeRange = this.timeRangeForSelectedService()
        const bookingsWithOrders = (this.bookings || [])
            .filter(b => !!b.order)
            .filter(booking => timeRange === null || booking.startsBetweenDates(timeRange[0], timeRange[1]))

        this.serviceDescription = timeRange !== null ? {
            name: this.service!.displayName,
            start: timeRange[0],
            end: timeRange[1],
        } : null

        const aggregates = this.aggregateBookings(bookingsWithOrders)
        this.aggregatedProducts = aggregates.products
        this.aggregatedModifiers = aggregates.modifiers
    }

    private timeRangeForSelectedService(): [Date, Date] | null {
        if (this.service === null) {
            return null
        }
        const servicesMatchingSelectedService = this.organisation
            .coalescedServicesUsedInVenueOnDate(this.venue, this.date)
            .filter(({ service }) => {
                return service.displayName === this.service!.displayName
            })
        if (servicesMatchingSelectedService.length === 0) {
            return null
        }
        const timeRange = servicesMatchingSelectedService[0].timeRange
        const endBookingSlot = new BookingSlot(timeRange[1])
        const endOfLastBookingSlot = endBookingSlot.endDateTime(this.venue.bookingInterval)
        return [timeRange[0], endOfLastBookingSlot]
    }

    private injectStylesToIframe() {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'styles.css'
        document.head.appendChild(link)
    }

    protected readonly DateTime = DateTime

    private aggregateBookings(bookings: Booking[]): { products: AggregatedEntry[]; modifiers: AggregatedEntry[] } {
        const productMap = new Map<string, AggregatedEntry>()
        const modifierMap = new Map<string, AggregatedEntry>()

        for (const booking of bookings) {
            const order = booking.order
            if (!order) { continue }

            for (const item of order.lineItems) {
                this.addOrUpdateAggregate(productMap, item.name, item.quantity, 'Unnamed Product')

                for (const modifier of item.modifiers || []) {
                    const quantity = modifier.quantity ?? item.quantity ?? 0
                    this.addOrUpdateAggregate(modifierMap, modifier.name, quantity, 'Unnamed Modifier')
                }
            }
        }

        return {
            products: this.mapToSortedArray(productMap),
            modifiers: this.mapToSortedArray(modifierMap),
        }
    }

    private addOrUpdateAggregate(
        map: Map<string, AggregatedEntry>,
        rawName: string | null | undefined,
        quantity: number | null | undefined,
        fallbackName: string,
    ) {
        const totalToAdd = quantity ?? 0
        if (totalToAdd <= 0) {
            return
        }

        const normalisedName = (rawName ?? '').trim() || fallbackName
        const existing = map.get(normalisedName)
        if (existing) {
            existing.totalQuantity += totalToAdd
        } else {
            map.set(normalisedName, {
                name: normalisedName,
                totalQuantity: totalToAdd,
            })
        }
    }

    private mapToSortedArray(map: Map<string, AggregatedEntry>): AggregatedEntry[] {
        return Array.from(map.values())
            .sort((a, b) => a.name.localeCompare(b.name))
    }
}
